"use client"

import * as React from "react"
import Image from "next/image"
import Link from "next/link"

import { ArenaMark } from "@/components/arena/v2/primitives"
import { Check } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { ConfettiBurst } from "@/components/ui/confetti-burst"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { TelegramIcon, WhatsAppIcon, XTwitterIcon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"
import { receiptFor } from "@/lib/wheel/grant"
import { SEGMENTS, type WheelPrize } from "@/lib/wheel/prizes"
import { segmentTint } from "./prize-wheel"
import type { PrizeRevealProps } from "@/components/wheel/types"

/* =============================================================================
   /wheel prize moment.

   A centred plate on the house field ground. The owner drives the phase:
   this renders for "revealed" (Claim) and "claimed" (receipt + share), and
   nothing else. The action slot is a fixed 68px so the plate never jumps
   between the two states. The grant itself happens in the owner; the
   receipt line here comes from receiptFor, which has no side effects.
   ============================================================================= */

const SHARE_URL = "https://go.getpicks.app/wheel"

/** Prizes that earn the confetti. The rest just shimmer. */
const CONFETTI_PRIZES = new Set(["credit-25", "credit-100", "lineup6", "secret"])

/* Verbatim from public/picks-mark.svg (see arena/v2/primitives). The ground
   mark comes from ArenaMark; the sealed-pack figure needs the same shape in
   solid white, which that component does not draw. */
const PICKS_MARK_PATH =
  "M24.3435 26.2618C24.559 26.3092 24.6638 26.5545 24.5492 26.7429L22.0537 30.843C21.9764 30.97 21.8221 31.0274 21.6805 30.9818L18.0184 29.8024C17.9448 29.7787 17.8822 29.7292 17.8421 29.6631L14.8521 24.7335C14.7055 24.4918 14.9198 24.1921 15.1959 24.2528L24.3435 26.2618ZM27.5723 21.7849C27.5039 21.8971 27.374 21.9564 27.2444 21.9345L11.6594 19.3037C11.5675 19.2882 11.4868 19.2335 11.4383 19.1539L8.71924 14.6873C8.58521 14.4671 8.75181 14.1866 9.00927 14.199L22.4215 14.8443C22.5176 14.8489 22.6107 14.8103 22.6752 14.739L29.1237 7.62029C29.3108 7.41366 29.1642 7.08301 28.8854 7.08301H10.0811C9.98691 7.08301 9.89743 7.12434 9.83635 7.19609L6.80359 10.7584C6.66169 10.9251 6.39832 10.9043 6.28435 10.7174L0.047105 0.48883C-0.0835217 0.274612 0.0706639 0 0.321567 0H40.2915C40.5425 0 40.6967 0.274685 40.566 0.4889L27.5723 21.7849Z"

/* Field recipe, creator-copy/field-card numbers with the pink lamp. */
const GROUND =
  "linear-gradient(118deg, hsl(0 0% 3%) 0%, hsl(0 0% 6%) 55%, hsl(0 0% 9%) 100%)"
const LAMP =
  "radial-gradient(130% 180% at 12% 4%, hsl(var(--primary-0) / 0.30) 0%, transparent 52%), linear-gradient(298deg, hsl(var(--black-500)) 0%, transparent 42%)"
const BLOOM =
  "radial-gradient(120% 150% at 94% 100%, hsl(var(--primary-0) / 0.12) 0%, transparent 52%)"
const SHIMMER =
  "linear-gradient(90deg, transparent 0%, hsl(var(--primary-0) / 0.45) 45%, hsl(0 0% 100% / 0.55) 55%, hsl(var(--primary-0) / 0.45) 65%, transparent 100%)"

function subFor(prize: WheelPrize): string {
  switch (prize.kind) {
    case "credit":
      return "Spends on any lineup"
    case "free_pick":
      return "Apply it in the slip"
    case "boost":
      return "Apply it in the slip, To win rises"
    case "lineup6":
      return "Six picks, entry on us"
    case "secret":
      return "Tear it open"
    case "respin":
      return "The wheel goes again"
    case "xp":
      return "Counts toward your creator rank"
  }
}

/** The sub line once the card is a post: the sharer's voice, for whoever
    sees it, not instructions for the sharer. */
function shareSubFor(prize: WheelPrize): string {
  switch (prize.kind) {
    case "credit":
      return "to play with, off one spin"
    case "free_pick":
      return "One free pick, off one spin"
    case "boost":
      return "on my next lineup, off one spin"
    case "lineup6":
      return "Six picks, entry covered, off one spin"
    case "secret":
      return "A sealed pack to tear open"
    case "respin":
      return "Another go at the wheel"
    case "xp":
      return "to my creator rank, off one spin"
  }
}

function creditFigure(value: number): string {
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`
}

const MINI_SEG = 360 / SEGMENTS.length
function miniPolar(r: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180
  return [100 + r * Math.sin(a), 100 - r * Math.cos(a)]
}
function miniSlice(i: number, r: number): string {
  const a0 = i * MINI_SEG - MINI_SEG / 2
  const [x0, y0] = miniPolar(r, a0)
  const [x1, y1] = miniPolar(r, a0 + MINI_SEG)
  return `M 100 100 L ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1} Z`
}

/** The wheel as it stopped: the winning slice up under the flapper, in pink,
    everything else dim. The object of the win, on the card that gets shared. */
export function MiniWheel({ prize, className }: { prize?: WheelPrize; className?: string }) {
  // No prize: the wheel at rest, nothing lit.
  const index = prize ? Math.max(0, SEGMENTS.indexOf(prize.id)) : -1
  const face = 82
  return (
    <svg viewBox="0 0 200 200" aria-hidden className={className}>
      <circle cx={100} cy={100} r={92} fill="hsl(var(--grey-900))" stroke="hsl(var(--primary))" strokeWidth={1} />
      <circle cx={100} cy={100} r={face} fill="hsl(var(--grey-800))" />
      <g transform={`rotate(${-index * MINI_SEG} 100 100)`}>
        {SEGMENTS.map((id, i) => (
          <path
            key={i}
            d={miniSlice(i, face)}
            fill={prize ? (i === index ? "hsl(var(--primary))" : i % 2 === 0 ? "transparent" : "hsl(var(--black-300))") : segmentTint(i)}
            stroke="hsl(var(--white-200))"
            strokeWidth={0.6}
          />
        ))}
        {Array.from({ length: SEGMENTS.length }, (_, i) => {
          const [px, py] = miniPolar(face - 4, (i + 0.5) * MINI_SEG)
          return <circle key={i} cx={px} cy={py} r={1.6} fill="hsl(var(--white-500))" />
        })}
      </g>
      <circle cx={100} cy={100} r={22} fill="hsl(var(--grey-950))" stroke="hsl(var(--white-300))" strokeWidth={0.6} />
      <g transform="translate(100 100) scale(0.7) translate(-20.35 -15.5)">
        <path d={PICKS_MARK_PATH} fill="hsl(var(--white-0))" />
      </g>
      <path d="M 94 4 L 106 4 L 100 30 Z" fill="hsl(var(--primary))" stroke="hsl(var(--grey-950))" strokeWidth={1} strokeLinejoin="round" />
      <circle cx={100} cy={8} r={4} fill="hsl(var(--grey-700))" stroke="hsl(var(--white-300))" strokeWidth={0.6} />
    </svg>
  )
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

const FIGURE = "block font-black leading-none tracking-[-0.04em] tabular-nums text-white"
const UNIT = "mt-1.5 block text-[11px] font-semibold tracking-tight text-white/55"

function PrizeFigure({ prize }: { prize: WheelPrize }) {
  if (prize.kind === "credit") {
    return (
      <>
        <span className={cn(FIGURE, "text-[52px] sm:text-[64px]")}>{creditFigure(prize.value ?? 0)}</span>
        <span className={UNIT}>credit</span>
      </>
    )
  }
  if (prize.kind === "secret") {
    return (
      <>
        <svg viewBox="0 0 40.7 31" aria-hidden className="block h-[64px] w-auto" fill="hsl(var(--white-900))">
          <path d={PICKS_MARK_PATH} />
        </svg>
        <span className={UNIT}>Sealed pack</span>
      </>
    )
  }
  return (
    <span className={cn(FIGURE, prize.label.length > 8 ? "text-[34px] sm:text-[40px]" : "text-[44px] sm:text-[56px]")}>
      {prize.label}
    </span>
  )
}

function ShareCircle({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={`Share to ${label}`}
      onClick={onClick}
      className="flex size-11 items-center justify-center rounded-full bg-grey-800 text-white transition-colors hover:bg-grey-700"
    >
      {children}
    </button>
  )
}

function ShareRow({ prize, onShared }: { prize: WheelPrize; onShared: () => void }) {
  const text = `I just spun ${prize.label} on Picks.`

  function openChannel(channel: "x" | "telegram" | "whatsapp") {
    const links: Record<typeof channel, string> = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(SHARE_URL)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${SHARE_URL}`)}`,
    }
    window.open(links[channel], "_blank", "noopener,noreferrer")
    onShared()
  }

  function copy() {
    navigator.clipboard.writeText(`${text} ${SHARE_URL}`).then(
      () => {
        toast.success("Link copied")
        onShared()
      },
      () => toast.error("Could not copy")
    )
  }

  return (
    <div className="flex w-full items-center gap-2">
      <ShareCircle label="X" onClick={() => openChannel("x")}>
        <XTwitterIcon className="size-4" />
      </ShareCircle>
      <ShareCircle label="Telegram" onClick={() => openChannel("telegram")}>
        <TelegramIcon className="size-[18px]" />
      </ShareCircle>
      <ShareCircle label="WhatsApp" onClick={() => openChannel("whatsapp")}>
        <WhatsAppIcon className="size-[18px]" />
      </ShareCircle>
      <button
        type="button"
        onClick={copy}
        className="ml-auto inline-flex h-11 items-center rounded-full bg-primary px-5 text-[12.5px] font-semibold tracking-tight text-white transition-[filter] hover:brightness-110"
      >
        Copy link
      </button>
    </div>
  )
}

/* ─── Main ───────────────────────────────────────────────────────────────── */

export function PrizeReveal({ prize, phase, onClaim, onShared, onDismiss }: PrizeRevealProps) {
  const [shared, setShared] = React.useState(false)
  const open = (phase === "revealed" || phase === "claimed") && prize !== null

  // A new prize means a new share.
  const prizeId = prize?.id ?? null
  React.useEffect(() => {
    setShared(false)
  }, [prizeId])

  if (!open || !prize) return null

  const receipt = receiptFor(prize)
  const isSecret = prize.kind === "secret"
  const isRespin = prize.kind === "respin"
  // Claimed = the card is now a post. Everything on it speaks to whoever
  // sees the screenshot, in the sharer's voice.
  const post = phase === "claimed"

  function handleShared() {
    setShared(true)
    onShared()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss()
      }}
    >
      <DialogContent
        showClose={false}
        className={cn(
          "w-[420px] max-w-[calc(100vw-32px)] gap-0 overflow-hidden rounded-[18px] border-0 bg-grey-900 p-0 shadow-none ring-1 ring-inset ring-white-100",
          "max-sm:bottom-auto max-sm:top-[50%] max-sm:translate-y-[-50%] max-sm:rounded-[18px]"
        )}
      >
        <DialogTitle className="sr-only">You won {prize.label}</DialogTitle>

        {/* The plate: field ground, lamp top-left, halftone, bloom, sunk mark. */}
        <div
          className="relative isolate flex min-h-[380px] w-full flex-col items-start justify-end overflow-hidden pt-5"
          style={{ background: GROUND }}
        >
          <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: LAMP }} />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
              backgroundSize: "7px 7px",
              opacity: 0.16,
              mixBlendMode: "overlay",
              maskImage: "linear-gradient(to right, black 0%, transparent 80%)",
              WebkitMaskImage: "linear-gradient(to right, black 0%, transparent 80%)",
            }}
          />
          <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: BLOOM }} />
          <ArenaMark
            className="pointer-events-none absolute -bottom-[6%] -right-[10%] aspect-[40.7/31] w-[86%]"
            alphaBoost={0.035}
          />
          {CONFETTI_PRIZES.has(prize.id) && <ConfettiBurst spread={240} delayMs={160} />}

          {/* Top row: the brand, then whose win this is. */}
          <div className="relative flex w-full items-center justify-between px-6 font-open-sauce">
            <span className="relative block h-[14px] w-[84px]">
              <Image src="/picks-logo.svg" alt="PICKS" fill sizes="84px" className="object-contain object-left" />
            </span>
            <span className="text-[11px] font-semibold tracking-tight text-primary">
              {shared ? "Shared, +1 spin" : post ? "I just spun" : "You won"}
            </span>
          </div>

          {/* The wheel as it stopped, right. */}
          <MiniWheel prize={prize} className="pointer-events-none absolute right-5 top-14 w-[112px] sm:w-[148px] motion-reduce:hidden" />

          <div className="relative mt-auto w-full px-6 pb-5 font-open-sauce">
            <div className="relative overflow-hidden">
              <PrizeFigure prize={prize} />
              <span
                aria-hidden
                className="promo-shimmer-sweep pointer-events-none absolute inset-y-0 left-0 w-1/2 motion-reduce:hidden"
                style={{ background: SHIMMER }}
              />
            </div>
            <p className="mt-2 text-[11px] leading-snug text-white-700">{post ? shareSubFor(prize) : subFor(prize)}</p>
          </div>

          {/* Foot bar, the field card's own: the pitch and where to go. The
              hook for whoever sees the screenshot. */}
          <div
            className="relative flex w-full items-center justify-between gap-3 border-t border-white-100 px-6 py-2.5 font-open-sauce text-[11px] leading-tight"
            style={{ background: "hsl(0 0% 4%)" }}
          >
            <span className="min-w-0 whitespace-nowrap text-white-700">
              Up to <span className="text-white">$100 credit</span> a spin
            </span>
            <span className="shrink-0 whitespace-nowrap font-semibold tracking-tight text-white">
              {post ? "Spin yours at " : ""}go.getpicks.app/wheel
            </span>
          </div>
        </div>

        {/* Action slot: fixed height so Claim and the receipt share one frame. */}
        <div className="px-5 pt-4">
          <div className="flex h-[68px] flex-col justify-center">
            {phase === "revealed" ? (
              <Button variant="primary" size="lg" className="w-full" onClick={onClaim}>
                {isSecret ? "Claim pack" : isRespin ? "Take the spin" : "Claim"}
              </Button>
            ) : (
              <>
                <p className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-grey-300">
                  <Check className="size-3.5 text-primary" />
                  {receipt.note}
                </p>
                <ShareRow prize={prize} onShared={handleShared} />
              </>
            )}
          </div>
          {phase === "claimed" && isSecret && (
            <Button asChild variant="outline" size="sm" className="mt-3 w-full">
              <Link href="/pack">Open pack</Link>
            </Button>
          )}
        </div>

        <div className="flex justify-end px-3 pb-2 pt-1">
          <Button variant="ghost" size="sm" className="text-grey-400 hover:text-white" onClick={onDismiss}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
